import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import * as Icons from '@ant-design/icons'
import { useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'

import type { APITypes } from '@qflow/api'

import { Select, LabeledValue } from 'portal/lib/sharedComponents/Select'
import {
  carriersLoad,
  carriersLoadContinuation
} from 'portal/store/wasteReferenceData/carriers/actions'

export interface EditWasteCarrierSelectorProps {
  onChange: () => void
  disabled?: boolean
  hidden?: boolean
}

type CarrierOption = LabeledValue & {
  carrier: APITypes.WasteCarrier
}

export const EditWasteCarrierSelector: FC<EditWasteCarrierSelectorProps> = ({
  onChange,
  disabled = false,
  hidden = false
}) => {
  const form = useFormContext()
  const dispatch = useDispatch()

  const [search, setSearch] = useState<string | null>(null)
  const {
    loading,
    hasNextPage,
    continuationToken,
    carriers,
    error
  }: {
    loading: boolean
    hasNextPage: boolean
    continuationToken: string
    carriers: APITypes.WasteCarrier[]
    error: any
  } = useSelector((state: any) => state.carriers)

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  useEffect(() => {
    dispatch(carriersLoad(search))
  }, [dispatch, search])

  const options = useMemo(() => {
    if (!carriers?.length) {
      return []
    }

    return carriers.map<CarrierOption>(carrier => {
      return {
        value: carrier.carrierLicenceId,
        label: `${carrier.name} (${carrier.carrierLicenceId})`,
        carrier
      }
    })
  }, [carriers])

  const handleNextPage = useCallback(() => {
    dispatch(carriersLoadContinuation(search, continuationToken))
  }, [dispatch, search, continuationToken])

  const handleSelect = useCallback(
    (label, value) => {
      const option = value as CarrierOption

      form.setValue('data.carrierLicenceId', option.carrier.carrierLicenceId, {
        shouldDirty: true
      })

      form.setValue('data.carrierName', option.carrier.name, {
        shouldDirty: true
      })

      if (onChange) {
        onChange()
      }
    },
    [form, onChange]
  )

  if (hidden) {
    return null
  }

  return (
    <Select<CarrierOption>
      id="editWasteCarrierSelector"
      showSearch
      loading={loading}
      placeholder={loading ? 'Fetching' : 'Select a Carrier to apply'}
      popupMatchSelectWidth={true}
      filter={false}
      options={options}
      onSearch={setSearch}
      onBottomReached={hasNextPage ? handleNextPage : undefined}
      labelInValue
      onSelect={handleSelect}
      suffixIcon={<Icons.SearchOutlined />}
      disabled={disabled}
    />
  )
}
